// Lead-magnet email-capture section for the Landing page.
//
// Sits above Pricing — "Free Wholesale Assignment Contract PDF" → captures
// email/name/state → POSTs to /api/lead-magnet/assignment-contract → on
// success, immediately offers the direct PDF download link + tells the
// user to check their inbox.
//
// All form state is local. No optimistic UI tricks — we want to know if
// the email send actually succeeded before showing the "check your inbox"
// success state.
import { useState } from "react";
import { FileText, Mail, MapPin, User as UserIcon, Download, CheckCircle2 } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

export default function LeadMagnetSection() {
  const [form, setForm] = useState({ name: "", email: "", state: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [captureId, setCaptureId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.email.includes("@") || form.name.trim().length < 1) {
      setErrorMsg("Please enter your name and a valid email.");
      return;
    }
    setStatus("submitting");
    try {
      const r = await fetch(`${backend}/api/lead-magnet/assignment-contract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          state: form.state || null,
          source: "landing",
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCaptureId(data.capture_id);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg("Something went wrong — try again or use the direct download below.");
    }
  };

  return (
    <section
      className="relative bg-gradient-to-br from-emerald-50 via-white to-emerald-50 py-24 border-t border-[var(--line)]"
      id="free-contract"
      data-testid="lead-magnet-section"
    >
      <div className="max-w-[1100px] mx-auto px-8">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          {/* LEFT — the pitch */}
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700 mb-3">
              <FileText size={14} /> Free Template · No credit card
            </div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.05] text-zinc-900 mb-5">
              Get our Wholesale<br />Assignment Contract.
            </h2>
            <p className="text-lg text-zinc-700 leading-relaxed mb-6">
              The exact template our community uses to assign deals — fillable, attorney-reviewed structure, ready to adapt for your state. Drop in your buyer, your seller, your fee. Done.
            </p>
            <ul className="space-y-2.5 text-sm text-zinc-700">
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" /> 10 standard clauses (Parties, Underlying Contract, Fee, Earnest Money, Closing, etc.)</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" /> Fillable fields for buyer, seller, property, assignment fee</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" /> Earnest-money handling checkboxes (reimbursed / credited / forfeited)</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" /> Delivered to your inbox + instant direct download</li>
            </ul>
          </div>

          {/* RIGHT — the form (or the success state) */}
          <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-7" data-testid="lead-magnet-card">
            {status !== "success" ? (
              <form onSubmit={submit} className="space-y-4">
                <h3 className="font-display font-bold text-2xl text-zinc-900 mb-1">Send me the contract</h3>
                <p className="text-sm text-zinc-500">We email it instantly. No spam — promise.</p>

                <label className="block">
                  <span className="text-xs font-semibold text-zinc-700 mb-1 inline-flex items-center gap-1.5"><UserIcon size={11} /> Your name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Marcus T."
                    required
                    data-testid="lead-magnet-name-input"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-700 mb-1 inline-flex items-center gap-1.5"><Mail size={11} /> Email</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="you@wholesale.com"
                    required
                    data-testid="lead-magnet-email-input"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-700 mb-1 inline-flex items-center gap-1.5"><MapPin size={11} /> State (optional)</span>
                  <select
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="lead-magnet-state-input"
                  >
                    <option value="">— Select state —</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>

                {errorMsg && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="lead-magnet-error">
                    {errorMsg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-zinc-900 font-bold text-sm px-5 py-3 rounded-full transition"
                  data-testid="lead-magnet-submit-btn"
                >
                  <Download size={14} /> {status === "submitting" ? "Sending…" : "Email me the contract"}
                </button>

                <p className="text-[11px] text-zinc-400 leading-relaxed">
                  By submitting, you agree to receive occasional emails from iSpeedToClose.
                  Unsubscribe any time. Not legal advice — have an attorney review before use.
                </p>
              </form>
            ) : (
              <div className="text-center py-4" data-testid="lead-magnet-success">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="font-display font-bold text-2xl text-zinc-900 mb-1">You're in.</h3>
                <p className="text-sm text-zinc-600 mb-5">
                  Check your inbox for the Assignment Contract. If you don't see it in a minute, peek at spam — or download directly:
                </p>
                <a
                  href={`${backend}/api/lead-magnet/assignment-contract.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm px-5 py-3 rounded-full transition"
                  data-testid="lead-magnet-direct-download"
                >
                  <Download size={14} /> Download the PDF
                </a>
                {captureId && (
                  <div className="text-[10px] text-zinc-400 mt-3">Capture id: {captureId.slice(0, 8)}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
